import { Card, Text, Stack, Group, Tooltip, Button } from '@mantine/core';
import { IconCircleCheck, IconAlertTriangle, IconArrowUp, IconPlus } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { LineChart, Line, YAxis, ResponsiveContainer } from 'recharts';
import { isWithinRange } from '@/hooks/useTargetRanges';
import { TargetRange } from '@/api/targetRanges';
import { useIsMobile } from '@/hooks/useIsMobile';
import { UnifiedManualEntryModal } from '@/components/UnifiedManualEntryModal';

interface MetricTileProps {
  label: string;
  value: number | null;
  unit: string;
  lastUpdated: string;
  color?: string;
  precision?: number;
  targetMin?: number;
  targetMax?: number;
  sparklineData?: Array<{ value: number | null }>;
  /** Parameter key for navigation (e.g., 'no3'). If set, tile is clickable. */
  parameterKey?: string;
  /**
   * Whether this parameter can be logged manually (i.e. it's in the
   * manual-entry parameter set). When true and the tile has no value, a small
   * "Log {name}" CTA opens the manual entry modal pre-selected to it.
   */
  loggable?: boolean;
}

/**
 * Get status color for value text.
 * In-range: no override (dark foreground). Out-of-range: coral accent.
 */
const getStatusColor = (status: 'in-range' | 'below' | 'above' | 'unknown'): string | undefined => {
  switch (status) {
    case 'below': return 'var(--rdh-accent)';
    case 'above': return 'var(--rdh-accent)';
    case 'in-range': return undefined;
    case 'unknown': return undefined;
  }
};

/**
 * Get status icon matching reference design.
 * In-range: teal circle-check. Out-of-range: coral alert/arrow icons.
 */
const getStatusIcon = (status: 'in-range' | 'below' | 'above' | 'unknown'): JSX.Element | null => {
  switch (status) {
    case 'in-range':
      return <IconCircleCheck size={16} style={{ color: 'var(--mantine-color-teal-6)' }} />;
    case 'below':
      return <IconAlertTriangle size={16} style={{ color: 'var(--rdh-accent)' }} />;
    case 'above':
      return <IconArrowUp size={16} style={{ color: 'var(--rdh-accent)' }} />;
    case 'unknown':
      return null;
  }
};

export function MetricTile({
  label,
  value,
  unit,
  lastUpdated,
  precision = 1,
  targetMin,
  targetMax,
  sparklineData,
  parameterKey,
  loggable,
}: MetricTileProps): JSX.Element {
  const isMobile = useIsMobile();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  // Show a "Log X" CTA on empty, manually-loggable tiles so a new user can turn
  // an N/A tile into a live one in ~30 seconds without hunting for the FAB.
  const showLogCta = value === null && !!loggable;

  // Runtime validation: Ensure value is a finite number before rendering
  // Prevents NaN, Infinity, or non-numeric values from being displayed
  const formattedValue =
    value !== null && typeof value === 'number' && isFinite(value)
      ? value.toFixed(precision)
      : 'N/A';

  const timeAgo = lastUpdated
    ? formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })
    : 'Unknown';

  // Build range object for helper function
  const range: Partial<TargetRange> | undefined = (targetMin !== undefined || targetMax !== undefined)
    ? {
        min_value: targetMin,
        max_value: targetMax,
      }
    : undefined;

  // Determine range status using helper function
  const rangeStatus = isWithinRange(value, range);
  const statusColor = getStatusColor(rangeStatus);
  const statusIcon = getStatusIcon(rangeStatus);

  // Build tooltip content with runtime validation
  const hasRange =
    targetMin !== undefined &&
    targetMax !== undefined &&
    typeof targetMin === 'number' &&
    typeof targetMax === 'number' &&
    isFinite(targetMin) &&
    isFinite(targetMax);
  const tooltipLabel = hasRange
    ? `Target range: ${targetMin!.toFixed(precision)} - ${targetMax!.toFixed(precision)} ${unit}`
    : null;

  // Wrap the status icon with a tooltip showing the target range
  const iconWithTooltip = statusIcon && hasRange ? (
    <Tooltip
      label={tooltipLabel}
      position="top"
      withArrow
      withinPortal
      events={{ hover: true, focus: true, touch: true }}
      offset={5}
    >
      <span style={{ display: 'inline-flex', cursor: 'help' }}>{statusIcon}</span>
    </Tooltip>
  ) : statusIcon;

  // Prepare sparkline chart data (filter nulls, need min 2 points)
  const sparklineChartData = sparklineData
    ?.map((d, i) => ({ index: i, value: d.value }))
    .filter(d => d.value !== null);
  const showSparkline = sparklineChartData && sparklineChartData.length >= 2;

  // Log CTA — an empty loggable tile renders the plain (non-anchor) card branch
  // below, so the button is a top-level interactive with no anchor to intercept.
  const logCta = showLogCta ? (
    <Button
      variant="light"
      size="compact-xs"
      leftSection={<IconPlus size={12} />}
      onClick={openModal}
      styles={{ root: { alignSelf: 'flex-start' } }}
    >
      {`Log ${label}`}
    </Button>
  ) : null;

  const cardStyle = {
    borderColor: rangeStatus === 'in-range'
      ? 'hsla(162, 73%, 46%, 0.3)'  // teal at 30% opacity
      : rangeStatus === 'unknown'
        ? undefined
        : 'hsla(0, 94%, 65%, 0.4)',   // red at 40% opacity
    borderWidth: 1,
    transition: 'border-color 200ms ease, box-shadow 200ms ease',
    cursor: parameterKey && !showLogCta ? 'pointer' : undefined,
    textDecoration: 'none' as const,
    color: 'inherit' as const,
    // Fill the sortable wrapper's flex column so every tile in the grid is
    // the same height; no-op when the card isn't in a flex context (e.g. the
    // DragOverlay copy).
    flex: 1,
    display: 'flex' as const,
    flexDirection: 'column' as const,
  };

  const cardBody = isMobile ? (
    // Mobile layout: Compact with time on same line as value. flex: 1 fills
    // the stretched card; the spacer pins the sparkline/CTA to the bottom so
    // equal-height tiles absorb spare space mid-card, not below the content.
    <Stack gap={4} style={{ flex: 1 }}>
      {/* Row 1: Label (left) + status icon (right) */}
      <Group gap="xs" justify="space-between">
        <Text size="xs" fw={500} c="dimmed">
          {label}
        </Text>
        {iconWithTooltip}
      </Group>
      {/* Row 2: Value+unit (left) + timeAgo (right) */}
      <Group gap="xs" justify="space-between" align="baseline">
        <Group gap={4} align="baseline">
          <Text size="md" fw={700} c={statusColor || undefined} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {formattedValue}
          </Text>
          <Text size="xs" c="dimmed" style={{ fontSize: 10 }}>
            {unit}
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          {timeAgo}
        </Text>
      </Group>
      <div style={{ flexGrow: 1 }} />
      {/* Row 3: Sparkline trend */}
      {showSparkline && (
        <div data-testid="sparkline-container" style={{ width: '100%', height: 28 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparklineChartData}>
              <YAxis domain={['dataMin', 'dataMax']} hide />
              <Line
                type="monotone"
                dataKey="value"
                stroke={rangeStatus === 'in-range' || rangeStatus === 'unknown' ? 'var(--mantine-color-teal-6)' : 'var(--rdh-accent)'}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {logCta}
    </Stack>
  ) : (
    // Desktop layout: Original vertical stack. flex: 1 fills the stretched
    // card; the spacer pins the sparkline/timestamp/CTA to the bottom so
    // sparklines align across a row of equal-height tiles.
    <Stack gap="xs" style={{ flex: 1 }}>
      {/* Label with optional status icon */}
      <Group gap="xs" justify="space-between">
        <Text size="sm" fw={500} c="dimmed">
          {label}
        </Text>
        {iconWithTooltip}
      </Group>

      {/* Value */}
      <Group gap="xs" align="baseline">
        <Text fw={700} c={statusColor || undefined} style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 24 }}>
          {formattedValue}
        </Text>
        <Text size="sm" c="dimmed">
          {unit}
        </Text>
      </Group>

      <div style={{ flexGrow: 1 }} />
      {/* Sparkline trend */}
      {showSparkline && (
        <div data-testid="sparkline-container" style={{ width: '100%', height: 32 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparklineChartData}>
              <YAxis domain={['dataMin', 'dataMax']} hide />
              <Line
                type="monotone"
                dataKey="value"
                stroke={rangeStatus === 'in-range' || rangeStatus === 'unknown' ? 'var(--mantine-color-teal-6)' : 'var(--rdh-accent)'}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Time Since */}
      <Text size="xs" c="dimmed" style={{ opacity: 0.6 }}>
        {timeAgo}
      </Text>
      {logCta}
    </Stack>
  );

  // An empty loggable tile drops its Link wrapper: a "Log X" Button cannot be
  // legally nested inside an anchor (invalid interactive nesting), and an empty
  // tile is a low-value navigation destination anyway. The tile keeps its Link
  // behavior as soon as it has a value.
  const card = parameterKey && !showLogCta ? (
    <Card
      component={Link}
      to={`/parameters/${parameterKey}`}
      padding={isMobile ? 'sm' : 'md'}
      withBorder
      style={cardStyle}
      className="metric-tile-clickable"
    >
      {cardBody}
    </Card>
  ) : (
    <Card
      padding={isMobile ? 'sm' : 'md'}
      withBorder
      style={cardStyle}
    >
      {cardBody}
    </Card>
  );

  return (
    <>
      {card}
      {showLogCta && (
        <UnifiedManualEntryModal
          opened={modalOpened}
          onClose={closeModal}
          initialType="water-test"
          defaultParameterKey={parameterKey}
        />
      )}
    </>
  );
}
